import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { selectIsLoading, selectSelectedPrediction } from '../../store/selectors/prediction.selectors';
import { CommonModule } from '@angular/common';
import { SkeletonModule } from 'primeng/skeleton';
import { PredictionService } from '../../services/prediction.service';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-details',
  imports: [ButtonModule, TableModule, ProgressBarModule, CommonModule, SkeletonModule],
  templateUrl: './details.component.html',
  styleUrl: './details.component.scss'
})
export class DetailsComponent {
  constructor(private router: Router) { }
  private store = inject(Store);
  private predictionService = inject(PredictionService)
  private messageService = inject(MessageService)

  prediction = toSignal(this.store.select(selectSelectedPrediction))
  loading = toSignal(this.store.select(selectIsLoading));
  isPredictionLiked = signal<boolean | null>(null);
  

  likeStats = signal<{
    like_percentage: number;
    dislike_percentage: number;
    total_votes?: number;
  } | null>(null);
  isLoadingStats = signal<boolean>(false);

  initializeLikeStatus = effect(() => {
    const prediction = this.prediction();
    if (prediction) {
      this.isPredictionLiked.set(prediction.isliked ?? null);
      this.loadLikeStats(prediction.id);
    }
  });

  markAsViewed = effect(() => {
    const prediction = this.prediction();
    const userId = JSON.parse(localStorage.getItem('user') || '{}').user_id;
    if (prediction?.id && userId) {
      this.predictionService.markPredictionAsViewed(prediction.id, userId).subscribe({
        next: () => console.log("Prediction marked as viewed"),
        error: err => console.error("Failed to mark prediction as viewed", err)
      });
    }
  });

  loadLikeStats(predictionId: number) {
    console.log("Loading like stats for prediction ID:", predictionId);
    if (!predictionId) return;
    
    this.isLoadingStats.set(true);
    this.predictionService.getPredictionLikeStats(predictionId).subscribe({
      next: (stats) => {
        this.likeStats.set({
          like_percentage: stats.like_percentage,
          dislike_percentage: stats.dislike_percentage
        });
        this.isLoadingStats.set(false);
      },
      error: (err) => {
        console.error('Failed to load like stats:', err);
        this.isLoadingStats.set(false);
        this.likeStats.set({
          like_percentage: 0,
          dislike_percentage: 0
        });
      }
    });
  }

  likePrediction(isLike: boolean) {
    const prediction = this.prediction();
    const userId = JSON.parse(localStorage.getItem('user') || '{}').user_id;
    
    if (!prediction?.id || !userId) {
      return;
    }

    const currentStatus = this.isPredictionLiked();

    if (currentStatus === isLike) {
      this.predictionService.removePredictionLikeOrDislike(prediction.id, userId).subscribe({
        next: () => {
          this.isPredictionLiked.set(null);
          this.messageService.add({
            severity: 'info',
            summary: 'Info',
            detail: 'Vous avez retiré votre préférence pour cette prédiction',
            life: 3000
          });
          this.loadLikeStats(prediction.id);
        }
      });
      

      return;
    }

    this.isPredictionLiked.set(isLike);

    const serviceCall = isLike 
      ? this.predictionService.likePredction(prediction.id, userId)
      : this.predictionService.dislikePredction(prediction.id, userId);

    serviceCall.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successful',
          detail: isLike ? 'Prédiction ajoutée aux favoris' : 'Prédiction retirée des favoris',
          life: 3000
        });
        this.loadLikeStats(prediction.id);
      },
      error: err => {
        this.isPredictionLiked.set(currentStatus);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: "Failed to update prediction preference",
          life: 3000
        });
      }
    });
  }

  goToDashboard() {
    this.router.navigate(['/']);
  }
}